import { Container, SelectList } from '@acies/core';
import { ObservableCollection } from '@acies/observables';
import type DropdownItem from './dropdown-item';
import DropdownList from './dropdown-list';
import { placePopover, type PopoverPlacement, setPopoverMaxHeight } from './popover-placement';
import theme from './theme';

/**
 * A Container that anchors a popover holding a SelectList, so a component can present a data set below itself in the top layer.
 *
 * The Item type defaults to DropdownItem, which is what the DropdownList the popover renders with presents. Sub classes that present another Item type set the type parameter and override createListChild().
 */
export default class PopoverContainer<Item = DropdownItem> extends Container {
	public static override observedAttributes: string[] = [...Container.observedAttributes, 'popover-height', 'popover-max-height', 'popover-max-width', 'popover-min-height', 'popover-min-width', 'popover-placement', 'popover-width'];
	public get anchorName(): string {
		return this.#anchorName;
	}

	public set data(value: Item[]) {
		if (this.#data === value) {
			return;
		}
		assertIsValidData<Item>(value);
		this.#data = value;
		this.#dataChanged = true;
		this.invalidateProperties();
	}

	public get data(): Item[] {
		return this.#data;
	}

	public get dataCollection(): ObservableCollection<Item> {
		if (!this.#dataCollection) {
			this.#dataCollection = new ObservableCollection();
		}
		return this.#dataCollection;
	}

	/**
	 * The height of the popover.
	 *
	 * If "hug", the popover shrinks to the height of its content, bounded by popoverMinHeight and popoverMaxHeight.
	 *
	 * If a number, it represents the height in pixels, and must be a non negative number.
	 */
	public set popoverHeight(value: 'hug' | number) {
		if (this.#popoverHeight === value) {
			return;
		}
		assertIsValidPopoverHeight(value);
		this.#popoverHeight = value;
		this.#popoverSizeChanged = true;
		this.invalidateProperties();
	}

	public get popoverHeight(): 'hug' | number {
		return this.#popoverHeight;
	}

	/**
	 * The maximum height of the popover in pixels.
	 *
	 * Pass Infinity to remove the limit.
	 */
	public set popoverMaxHeight(value: number) {
		if (this.#popoverMaxHeight === value) {
			return;
		}
		assertIsValidPopoverLength(value);
		this.#popoverMaxHeight = value;
		this.#popoverSizeChanged = true;
		this.invalidateProperties();
	}

	public get popoverMaxHeight(): number {
		return this.#popoverMaxHeight;
	}

	/**
	 * The maximum width of the popover.
	 *
	 * If "anchor", the popover never grows wider than this component (the anchor).
	 *
	 * If a number, it represents the maximum width in pixels. Pass Infinity to remove the limit.
	 */
	public set popoverMaxWidth(value: 'anchor' | number) {
		if (this.#popoverMaxWidth === value) {
			return;
		}
		assertIsValidPopoverWidthLength(value);
		this.#popoverMaxWidth = value;
		this.#popoverSizeChanged = true;
		this.invalidateProperties();
	}

	public get popoverMaxWidth(): 'anchor' | number {
		return this.#popoverMaxWidth;
	}

	/** The minimum height of the popover in pixels. */
	public set popoverMinHeight(value: number) {
		if (this.#popoverMinHeight === value) {
			return;
		}
		assertIsValidPopoverLength(value);
		this.#popoverMinHeight = value;
		this.#popoverSizeChanged = true;
		this.invalidateProperties();
	}

	public get popoverMinHeight(): number {
		return this.#popoverMinHeight;
	}

	/**
	 * The minimum width of the popover.
	 *
	 * If "anchor", the popover is at least as wide as this component (the anchor), which combined with popoverWidth "hug" lets the popover grow wider than the anchor when the content needs it.
	 *
	 * If a number, it represents the minimum width in pixels.
	 */
	public set popoverMinWidth(value: 'anchor' | number) {
		if (this.#popoverMinWidth === value) {
			return;
		}
		assertIsValidPopoverWidthLength(value);
		this.#popoverMinWidth = value;
		this.#popoverSizeChanged = true;
		this.invalidateProperties();
	}

	public get popoverMinWidth(): 'anchor' | number {
		return this.#popoverMinWidth;
	}

	/**
	 * The side of this component the popover opens on, when there is room for it there.
	 *
	 * "bottom" by default. Where there is no room, the browser moves the popover to the opposite side, then, for "left" and "right", below or above. Should there be room for all of it nowhere, it shrinks to fit below or above, where there is the most room.
	 */
	public set popoverPlacement(value: PopoverPlacement) {
		if (this.#popoverPlacement === value) {
			return;
		}
		assertIsValidPopoverPlacement(value);
		this.#popoverPlacement = value;
		this.#popoverPlacementChanged = true;
		this.invalidateProperties();
	}

	public get popoverPlacement(): PopoverPlacement {
		return this.#popoverPlacement;
	}

	/**
	 * The width of the popover.
	 *
	 * If "anchor", the popover width matches the width of this component (the anchor).
	 *
	 * If "hug", the popover shrinks to the width of its content, bounded by popoverMinWidth and popoverMaxWidth.
	 *
	 * If a number, it represents the width in pixels, and must be a non negative number.
	 */
	public set popoverWidth(value: 'anchor' | 'hug' | number) {
		if (this.#popoverWidth === value) {
			return;
		}
		assertIsValidPopoverWidth(value);
		this.#popoverWidth = value;
		this.#popoverSizeChanged = true;
		this.invalidateProperties();
	}

	public get popoverWidth(): 'anchor' | 'hug' | number {
		return this.#popoverWidth;
	}

	public get state(): 'closed' | 'open' {
		return this.#state;
	}

	protected get listChild(): SelectList<Item> {
		if (!this.#listChild) {
			this.#listChild = this.createListChild();
			this.#listChild.data = this.dataCollection;
		}
		return this.#listChild;
	}

	/**
	 * The Container that is rendered in the top layer by the popover API.
	 *
	 * Sub classes can reach it for one off customization like padding or fill, but the sizing styles are owned by the popover* properties, since the Component sizing pipeline resets style.width and style.height whenever the parentAutoLayout of this child changes.
	 */
	protected get popoverChild(): Container {
		if (!this.#popoverChild) {
			this.#popoverChild = new Container();
			this.#popoverChild.fill = theme.colors.fill.white;
			this.#popoverChild.cornerRadius = 4;
			this.#popoverChild.stroke = theme.strokes.soft;
			this.#popoverChild.hideScrollbars = true;
			this.#popoverChild.effects = [theme.effects.shadows.light, theme.effects.shadows.medium];
			this.#popoverChild.popover = 'auto';
			// Chrome makes a scroll container keyboard focusable unless it has focusable children
			// or has said no itself, so a popover whose list overflows would otherwise be a tab
			// stop of its own: an invisible one, between the anchor and whatever follows it. The
			// arrow keys reach the items, and focusing one scrolls it into view, so nothing is
			// lost by taking the panel out of the tab order.
			this.#popoverChild.tabIndex = -1;
			// remove default black border that is set when using the .popover API
			this.#popoverChild.style.border = 'none';
			// fixed, as the popover user agent style has it, rather than the relative a Component
			// starts with: the top layer would lay that out as absolute, against the document rather
			// than the viewport, and the room on each side is meant to be the room the user can see
			this.#popoverChild.position = 'fixed';
			this.#popoverChild.style.positionAnchor = this.#anchorName;
			// 4px from the side of this component popoverPlacement prefers, or another side with more room
			placePopover(this.#popoverChild, this.#popoverPlacement);
			this.#popoverChild.addEventListener('beforetoggle', this.beforeToggle.bind(this) as EventListener);
			this.#popoverChild.addEventListener('toggle', this.toggle.bind(this) as EventListener);
			this.#popoverChild.addComponent(this.listChild);
		}
		return this.#popoverChild;
	}

	#anchorName: string;
	#data: Item[];
	#dataChanged: boolean;
	#dataCollection!: ObservableCollection<Item>;
	#listChild!: SelectList<Item>;
	#popoverChild!: Container;
	#popoverHeight: 'hug' | number;
	#popoverMaxHeight: number;
	#popoverMaxWidth: 'anchor' | number;
	#popoverMinHeight: number;
	#popoverMinWidth: 'anchor' | number;
	#popoverPlacement: PopoverPlacement;
	#popoverPlacementChanged: boolean;
	#popoverSizeChanged: boolean;
	#popoverWidth: 'anchor' | 'hug' | number;
	#showWanted: boolean;

	#state: 'closed' | 'open';

	public constructor() {
		super();
		this.minWidth = 240;
		this.height = 32;
		// we randomize the anchorName since multiple popovers wouldn't know
		// what "anchor" to position itself against
		// Math.random() didn't work directly, maybe the punctuation, so we just create an integer value
		this.#anchorName = `--${Math.round(Math.random() * 1000000 + 1)}`;
		this.#state = 'closed';
		this.#showWanted = false;
		this.#data = [];
		this.#dataChanged = false;
		this.#popoverHeight = 'hug';
		// a maxHeight that will "cut" the last viewable DropdownItemRenderer in half,
		// so the user gets a visual hint that the list can be scrolled
		this.#popoverMaxHeight = 204;
		this.#popoverMaxWidth = Infinity;
		this.#popoverMinHeight = 32;
		this.#popoverMinWidth = 0;
		this.#popoverPlacement = 'bottom';
		this.#popoverPlacementChanged = false;
		// true, so the popover child gets its sizing styles on the first commit
		this.#popoverSizeChanged = true;
		this.#popoverWidth = 'anchor';
	}

	/**
	 * Asks for the popover to be closed.
	 *
	 * @see {@link show} for why this is a wish rather than an order.
	 */
	public hide(): void {
		this.#showWanted = false;
		this.#applyPopoverVisibility();
	}

	/**
	 * Asks for the popover to be shown, which it is as soon as there is something to show.
	 *
	 * A wish rather than an order, because a popover holds a list and an empty list is nothing: a component that asks the moment the user acts, and fills the list once an API has answered, would otherwise flash an empty panel for as long as the answer takes.
	 *
	 * The wish is remembered, so the popover opens by itself when the data arrives. It is dropped again when the popover closes for any reason, the user clicking elsewhere included.
	 */
	public show(): void {
		this.#showWanted = true;
		this.#applyPopoverVisibility();
	}

	/** Asks for the opposite of what was last asked for. */
	public showHide(): void {
		this.#showWanted = !this.#showWanted;
		this.#applyPopoverVisibility();
	}

	protected override attributeChangedCallback(name: string, _: string, value: string) {
		super.attributeChangedCallback(name, _, value);
		if (name === 'popover-height') {
			this.#popoverHeightAttributeChanged(value);
		} else if (name === 'popover-max-height') {
			this.#popoverMaxHeightAttributeChanged(value);
		} else if (name === 'popover-max-width') {
			this.#popoverMaxWidthAttributeChanged(value);
		} else if (name === 'popover-min-height') {
			this.#popoverMinHeightAttributeChanged(value);
		} else if (name === 'popover-min-width') {
			this.#popoverMinWidthAttributeChanged(value);
		} else if (name === 'popover-placement') {
			this.#popoverPlacementAttributeChanged(value);
		} else if (name === 'popover-width') {
			this.#popoverWidthAttributeChanged(value);
		}
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#dataChanged) {
			this.#commitData();
		}
		if (this.#popoverPlacementChanged) {
			this.#commitPopoverPlacement();
		}
		if (this.#popoverSizeChanged) {
			this.#commitPopoverSize();
		}
	}

	/**
	 * Creates the SelectList the popover renders the data with.
	 *
	 * Override in sub classes that present another Item type than DropdownItem, and return the SelectList sub class that renders it. The list is created once, the first time the popover child is read, so an override is always in place before it is called.
	 */
	protected createListChild(): SelectList<Item> {
		// DropdownList is a SelectList<DropdownItem>, which only satisfies SelectList<Item> while
		// Item is left at its DropdownItem default — which is the only case this default is reached
		return new DropdownList() as unknown as SelectList<Item>;
	}

	protected override initialize(): void {
		super.initialize();
		this.style.anchorName = this.#anchorName;
		this.addComponent(this.popoverChild);
		// setting display to '' resets, so the popover display style kicks in
		this.popoverChild.style.display = '';
	}

	#commitData(): void {
		this.#dataChanged = false;
		this.dataCollection.clear();
		this.dataCollection.addItems(this.data);
		// after the list has the items, since whether the popover may open is judged on whether
		// there is anything in it. Data arriving is what opens a popover that was asked for while
		// there was nothing, and data going away is what closes one that has nothing left to show
		this.#applyPopoverVisibility();
	}

	/**
	 * Opens the popover when it is both wanted and worth opening, and closes it otherwise.
	 *
	 * What counts as worth opening is what the list would actually render, so a filter that matches nothing closes the popover as surely as no data at all.
	 *
	 * The popover's own state is read from the DOM rather than from {@link state}, which is only caught up once the browser has fired its toggle event: showing a popover that is already showing throws, and this runs again the moment data arrives.
	 */
	#applyPopoverVisibility(): void {
		const shouldShow = this.#showWanted && this.dataCollection.length > 0;
		const isShowing = this.popoverChild.matches(':popover-open');
		if (shouldShow && !isShowing) {
			this.popoverChild.showPopover();
		} else if (!shouldShow && isShowing) {
			this.popoverChild.hidePopover();
		}
	}

	#commitPopoverPlacement(): void {
		this.#popoverPlacementChanged = false;
		placePopover(this.popoverChild, this.#popoverPlacement);
	}

	/**
	 * The popover sizing is written directly as styles instead of going through the Component sizing properties, because a width can be an anchor-size() expression, that "fill" | "hug" | number cannot express.
	 *
	 * This must run after the popover child has initialized, since Component.initialize() resets the min and max sizing styles to their defaults.
	 */
	#commitPopoverSize(): void {
		this.#popoverSizeChanged = false;
		const { style } = this.popoverChild;
		style.height = popoverSizeToStyle(this.#popoverHeight);
		setPopoverMaxHeight(this.popoverChild, this.#popoverMaxHeight);
		style.maxWidth = popoverSizeToStyle(this.#popoverMaxWidth);
		style.minHeight = popoverSizeToStyle(this.#popoverMinHeight);
		style.minWidth = popoverSizeToStyle(this.#popoverMinWidth);
		style.width = popoverSizeToStyle(this.#popoverWidth);
	}

	#popoverHeightAttributeChanged(value: string): void {
		if (value === 'hug') {
			this.popoverHeight = value;
		} else {
			this.popoverHeight = Number(value);
		}
	}

	#popoverMaxHeightAttributeChanged(value: string): void {
		this.popoverMaxHeight = Number(value);
	}

	#popoverMaxWidthAttributeChanged(value: string): void {
		if (value === 'anchor') {
			this.popoverMaxWidth = value;
		} else {
			this.popoverMaxWidth = Number(value);
		}
	}

	#popoverMinHeightAttributeChanged(value: string): void {
		this.popoverMinHeight = Number(value);
	}

	#popoverMinWidthAttributeChanged(value: string): void {
		if (value === 'anchor') {
			this.popoverMinWidth = value;
		} else {
			this.popoverMinWidth = Number(value);
		}
	}

	#popoverPlacementAttributeChanged(value: string): void {
		this.popoverPlacement = value as PopoverPlacement;
	}

	#popoverWidthAttributeChanged(value: string): void {
		if (value === 'anchor' || value === 'hug') {
			this.popoverWidth = value;
		} else {
			this.popoverWidth = Number(value);
		}
	}

	private beforeToggle(event: ToggleEvent): void {
		if (event.newState === 'open') {
			// invalidate the autoLayout so we set the style.display correctly
			this.popoverChild.autoLayout = 'vertical';
			this.popoverChild.autoLayout = 'horizontal';
			// the Component sizing pipeline resets style.width and style.height when the
			// parentAutoLayout of the popover child changes, so we re-apply our own sizing
			// right before the popover becomes visible
			this.#commitPopoverSize();
		} else {
			// setting display to '' resets, so the popover display style kicks in
			this.popoverChild.style.display = '';
		}
	}

	private toggle(event: ToggleEvent): void {
		this.#state = event.newState as 'closed' | 'open';
		// the browser closes a popover on a click outside or on Escape, and that is the user saying
		// they are done with it. Forgetting the wish here is what stops the next data from putting
		// it back on screen behind their back
		if (this.#state === 'closed') {
			this.#showWanted = false;
		}
	}
}
customElements.define('ac-popover-container', PopoverContainer);

function assertIsValidData<Item>(value: unknown): asserts value is Item[] {
	if (Array.isArray(value)) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be an Array`);
}

function assertIsValidPopoverHeight(value: unknown): asserts value is 'hug' | number {
	if (value === 'hug') {
		return;
	}
	if (typeof value === 'number' && value >= 0) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be a non negative number or "hug"`);
}

function assertIsValidPopoverLength(value: unknown): asserts value is number {
	if (typeof value === 'number' && value >= 0) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be a non negative number`);
}

function assertIsValidPopoverPlacement(value: unknown): asserts value is PopoverPlacement {
	if (value === 'bottom' || value === 'left' || value === 'right' || value === 'top') {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be "bottom", "left", "right" or "top"`);
}

function assertIsValidPopoverWidth(value: unknown): asserts value is 'anchor' | 'hug' | number {
	if (value === 'anchor' || value === 'hug') {
		return;
	}
	if (typeof value === 'number' && value >= 0) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be a non negative number, "anchor" or "hug"`);
}

function assertIsValidPopoverWidthLength(value: unknown): asserts value is 'anchor' | number {
	if (value === 'anchor') {
		return;
	}
	if (typeof value === 'number' && value >= 0) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be a non negative number or "anchor"`);
}

/**
 * Translates a popover sizing value into a CSS length.
 *
 * "hug" lets the content decide, and Infinity removes a min / max constraint, so both resolve to an empty string that resets the style to its initial value.
 */
function popoverSizeToStyle(value: 'anchor' | 'hug' | number): string {
	if (value === 'anchor') {
		return 'anchor-size(width)';
	}
	if (value === 'hug' || !Number.isFinite(value)) {
		return '';
	}
	return `${value}px`;
}
